import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { exchangeForCustomToken, signIntoFirebase } from "./firebase/auth.js";
import { initI18n } from "./i18n/init.js";
import { loadEnv } from "./lib/env.js";
import Lobby from "./routes/lobby.js";
import Sheet from "./routes/sheet.js";
import Table from "./routes/table.js";
import { getDiscordAuth } from "./sdk/discord.js";
import { useAuthStore } from "./stores/auth.js";

initI18n();

function AuthBoot({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const ready = useAuthStore((s) => s.ready);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    (async () => {
      try {
        const env = loadEnv();
        const discord = await getDiscordAuth({
          clientId: env.discordClientId,
          tokenExchange: async () => ({ access_token: "" }),
        });
        const mint = await exchangeForCustomToken({
          brokerUrl: env.authBrokerUrl,
          discordAccessToken: discord.accessToken,
        });
        await signIntoFirebase(mint.firebase_custom_token);
        if (cancelled) return;
        setAuth(discord.user.id, null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, setAuth]);

  if (error)
    return <p className="p-4 text-destructive">{t("app.auth.failed", { reason: error })}</p>;
  if (!ready) return <p className="p-4">{t("app.auth.connecting")}</p>;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <AuthBoot>
          <Routes>
            <Route path="/" element={<Lobby />} />
            <Route path="/table" element={<Table />} />
            <Route path="/sheet/:charId" element={<Sheet />} />
          </Routes>
        </AuthBoot>
      </Suspense>
    </BrowserRouter>
  );
}
